import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, 
  Star,
  Shield,
  Zap,
  Globe,
  Smartphone,
  CheckCircle,
  ExternalLink,
  Loader,
  AlertCircle,
  ShoppingCart,
  Heart,
  Phone,
  Mail,
  MapPin,
  Clock,
  Gift,
  CreditCard,
  Store,
  Lock
} from 'lucide-react'

const ProductDetail: React.FC = () => {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [product, setProduct] = useState(null)
  const [loading, setLoading] = useState(false)
  const [selectedImage, setSelectedImage] = useState(0)
  const [isBookmarked, setIsBookmarked] = useState(false)

  // 静态产品数据 - 根据不同的产品ID展示不同内容
  const products = {
    'coretrust': {
      _id: 'coretrust',
      name: 'CoreTrust手机',
      slug: 'coretrust',
      description: 'CoreTrust是DeepLink Limited推出的旗舰级Web3智能手机，集成了六大核心功能，为用户提供完整的Web3生态系统解决方案。',
      shortDescription: '旗舰级Web3智能手机，六大核心功能深度集成',
      category: '手机',
      price: {
        current: 8999,
        original: 9999,
        currency: 'CNY'
      },
      availability: 'in_stock',
      features: [
        {
          title: '熄屏广告系统',
          description: '智能熄屏显示，精准投放广告内容，支持高颗粒度广告投放和管控',
          icon: 'Shield',
          details: [
            '精确到单个设备具体时间段',
            '精确定位精准潜在用户',
            '基于用户行为数据的智能投放',
            '广告收入分成模式'
          ]
        },
        {
          title: '空投助手',
          description: '官方优选Web3优质项目代币空投活动，一键参与',
          icon: 'Gift',
          details: [
            '自动筛选优质项目',
            '一键参与空投活动',
            '风险评级和收益预测',
            '空投项目方合作分成'
          ]
        },
        {
          title: '数字银行',
          description: '打通数字货币与法币通道，完成数字货币的无感消费',
          icon: 'CreditCard',
          details: [
            '实时汇率转换',
            '多币种支持',
            '无感支付体验',
            '交易手续费收入'
          ]
        },
        {
          title: '硬件级钱包',
          description: '提供芯片级数据加密存储安全的多链钱包',
          icon: 'Lock',
          details: [
            '芯片级数据加密',
            '支持完全离线模式',
            '硬件及网络隔离',
            '多链支持'
          ]
        },
        {
          title: '应用市场',
          description: '打通区块链应用下载通道，垂直领域应用管理',
          icon: 'Store',
          details: [
            '去中心化应用商店',
            '智能合约验证',
            '垂直领域分类',
            '应用分成收入'
          ]
        },
        {
          title: '去中心化网络通话',
          description: '基于钱包地址拨打作为通话目标实现全球通话',
          icon: 'Phone',
          details: [
            '钱包地址作为通话ID',
            '全球通话网络',
            '去中心化通信协议',
            '通话费用透明化'
          ]
        }
      ],
      specifications: [
        {
          category: '硬件规格',
          items: [
            { name: '处理器', value: '高通骁龙8 Gen 2' },
            { name: '内存', value: '12GB LPDDR5X' },
            { name: '存储', value: '256GB/512GB UFS 4.0' },
            { name: '屏幕', value: '6.7英寸 2K+ 120Hz OLED' },
            { name: '电池', value: '5000mAh 120W快充' },
            { name: '摄像头', value: '5000万像素三摄系统' }
          ]
        },
        {
          category: 'Web3功能',
          items: [
            { name: '区块链网络', value: '支持Ethereum、BSC、Polygon等主流网络' },
            { name: '钱包功能', value: '硬件级安全芯片保护' },
            { name: 'DeFi集成', value: '内置主流DeFi协议' },
            { name: 'NFT支持', value: '原生NFT展示和管理' },
            { name: '跨链功能', value: '支持多链资产转移' }
          ]
        },
        {
          category: '安全特性',
          items: [
            { name: '生物识别', value: '指纹+面部识别' },
            { name: '硬件安全', value: '独立安全芯片' },
            { name: '数据加密', value: '端到端加密存储' },
            { name: '隐私保护', value: '零知识证明技术' }
          ]
        }
      ],
      images: [
        { url: '/images/products/coretrust-1.jpg', alt: 'CoreTrust 正面', type: 'main' },
        { url: '/images/products/coretrust-2.jpg', alt: 'CoreTrust 背面', type: 'gallery' },
        { url: '/images/products/coretrust-3.jpg', alt: 'CoreTrust 侧面', type: 'gallery' }
      ],
      tags: ['Web3', '智能手机', '区块链', 'DeFi', 'NFT']
    },
    'ads': {
      _id: 'ads',
      name: '熄屏广告系统',
      slug: 'ads',
      description: '熄屏广告系统是CoreTrust手机的核心功能之一，提供高颗粒度的广告投放和管控，精确到单个设备具体时间段，实现精准营销。',
      shortDescription: '高颗粒度广告投放和管控系统',
      category: '软件',
      price: null, // 内置功能，不显示价格
      availability: 'in_stock',
      features: [
        {
          title: '精准投放',
          description: '基于用户行为数据和设备信息，实现广告的精准投放',
          icon: 'Zap',
          details: [
            '用户画像分析',
            '行为数据追踪',
            '兴趣标签匹配',
            '实时投放优化'
          ]
        },
        {
          title: '时间控制',
          description: '精确到单个设备具体时间段的广告投放控制',
          icon: 'Clock',
          details: [
            '时间段精准控制',
            '设备级别管理',
            '投放频率限制',
            '用户习惯学习'
          ]
        },
        {
          title: '收益分成',
          description: '透明的收益分成机制，让用户和平台共同受益',
          icon: 'CreditCard',
          details: [
            '透明收益分配',
            '实时收益统计',
            '多种提现方式',
            '收益历史记录'
          ]
        }
      ],
      specifications: [
        {
          category: '技术规格',
          items: [
            { name: '投放精度', value: '设备级别，精确到秒' },
            { name: '响应时间', value: '< 100ms' },
            { name: '支持格式', value: '图片、视频、交互式广告' },
            { name: '数据安全', value: '端到端加密' }
          ]
        }
      ],
      images: [
        { url: '/images/products/ads-1.jpg', alt: '熄屏广告展示', type: 'main' },
        { url: '/images/products/ads-2.jpg', alt: '广告管理界面', type: 'gallery' }
      ],
      tags: ['广告', '营销', '精准投放', '收益分成']
    },
    'airdrop': {
      _id: 'airdrop',
      name: '空投助手',
      slug: 'airdrop',
      description: '空投助手是CoreTrust手机的重要功能，帮助用户发现和参与Web3优质项目的代币空投活动，提供风险评级和收益预测。',
      shortDescription: 'Web3项目空投活动发现和参与工具',
      category: '软件',
      price: null, // 内置功能，不显示价格
      availability: 'in_stock',
      features: [
        {
          title: '项目筛选',
          description: '自动筛选优质Web3项目，确保空投活动的真实性和价值',
          icon: 'Shield',
          details: [
            '项目背景调查',
            '团队实力评估',
            '技术方案分析',
            '市场前景预测'
          ]
        },
        {
          title: '一键参与',
          description: '简化空投参与流程，一键完成复杂的空投任务',
          icon: 'Zap',
          details: [
            '任务自动执行',
            '多账户管理',
            '批量操作支持',
            '进度实时跟踪'
          ]
        },
        {
          title: '风险评级',
          description: '专业的风险评估系统，帮助用户识别和规避风险',
          icon: 'Shield',
          details: [
            '多维度风险评估',
            '历史数据分析',
            '风险等级分类',
            '预警机制'
          ]
        }
      ],
      specifications: [
        {
          category: '功能特性',
          items: [
            { name: '支持项目', value: '1000+ Web3项目' },
            { name: '成功率', value: '95%以上' },
            { name: '更新频率', value: '实时更新' },
            { name: '安全等级', value: '银行级安全' }
          ]
        }
      ],
      images: [
        { url: '/images/products/airdrop-1.jpg', alt: '空投助手界面', type: 'main' },
        { url: '/images/products/airdrop-2.jpg', alt: '项目筛选页面', type: 'gallery' }
      ],
      tags: ['空投', 'DeFi', '项目发现', '风险控制']
    },
    'bank': {
      _id: 'bank',
      name: '数字银行',
      slug: 'bank',
      description: '数字银行功能打通数字货币与法币通道，让用户能够使用数字货币进行日常消费，实现真正的数字货币无感消费体验。',
      shortDescription: '数字货币与法币的无感转换和消费',
      category: '软件',
      price: null, // 内置功能，不显示价格
      availability: 'in_stock',
      features: [
        {
          title: '实时汇率',
          description: '提供实时、准确的数字货币与法币汇率转换',
          icon: 'Globe',
          details: [
            '多交易所价格聚合',
            '实时汇率更新',
            '历史汇率查询',
            '汇率趋势分析'
          ]
        },
        {
          title: '多币种支持',
          description: '支持主流数字货币的存储、转换和消费',
          icon: 'CreditCard',
          details: [
            'BTC、ETH等主流币种',
            '稳定币支持',
            '跨链资产管理',
            '自动币种转换'
          ]
        },
        {
          title: '无感支付',
          description: '在传统商户中直接使用数字货币支付，无需额外操作',
          icon: 'Smartphone',
          details: [
            'NFC近场支付',
            '二维码支付',
            '在线购物支付',
            '自动法币转换'
          ]
        }
      ],
      specifications: [
        {
          category: '服务特性',
          items: [
            { name: '支持币种', value: '50+ 主流数字货币' },
            { name: '转换速度', value: '< 3秒' },
            { name: '手续费', value: '0.1%' },
            { name: '安全等级', value: '银行级加密' }
          ]
        }
      ],
      images: [
        { url: '/images/products/bank-1.jpg', alt: '数字银行界面', type: 'main' },
        { url: '/images/products/bank-2.jpg', alt: '支付流程展示', type: 'gallery' }
      ],
      tags: ['数字银行', '支付', '汇率', '消费']
    },
    'wallet': {
      _id: 'wallet',
      name: '硬件钱包',
      slug: 'wallet',
      description: '硬件级钱包提供芯片级数据加密存储安全的多链钱包，支持完全离线模式，为用户资产提供最高级别的安全保护。',
      shortDescription: '芯片级安全的多链硬件钱包',
      category: '软件',
      price: null, // 内置功能，不显示价格
      availability: 'in_stock',
      features: [
        {
          title: '芯片级加密',
          description: '使用独立安全芯片，提供硬件级别的数据加密保护',
          icon: 'Lock',
          details: [
            '独立安全芯片',
            '硬件级加密算法',
            '私钥永不离开设备',
            '防篡改设计'
          ]
        },
        {
          title: '离线模式',
          description: '支持完全离线模式，私钥永远不会接触网络',
          icon: 'Shield',
          details: [
            '冷钱包模式',
            '离线签名',
            '硬件隔离',
            '网络断连保护'
          ]
        },
        {
          title: '多链支持',
          description: '支持主流区块链网络，统一管理多链资产',
          icon: 'Globe',
          details: [
            'Ethereum生态',
            'BSC网络',
            'Polygon网络',
            '其他主流公链'
          ]
        }
      ],
      specifications: [
        {
          category: '安全规格',
          items: [
            { name: '加密等级', value: 'AES-256 + ECC-256' },
            { name: '安全芯片', value: '独立安全芯片' },
            { name: '支持网络', value: '20+ 区块链网络' },
            { name: '认证标准', value: 'FIDO2 + CC EAL5+' }
          ]
        }
      ],
      images: [
        { url: '/images/products/wallet-1.jpg', alt: '硬件钱包展示', type: 'main' },
        { url: '/images/products/wallet-2.jpg', alt: '安全芯片特写', type: 'gallery' }
      ],
      tags: ['硬件钱包', '安全', '加密', '多链']
    },
    'market': {
      _id: 'market',
      name: '应用市场',
      slug: 'market',
      description: 'Web3应用市场打通区块链应用下载通道，提供垂直领域应用管理，确保应用的安全性和可靠性。',
      shortDescription: '去中心化的Web3应用商店',
      category: '软件',
      price: null, // 内置功能，不显示价格
      availability: 'in_stock',
      features: [
        {
          title: '去中心化',
          description: '基于区块链的去中心化应用商店，无需中心化审核',
          icon: 'Globe',
          details: [
            '智能合约验证',
            '社区治理',
            '去中心化存储',
            '透明化运营'
          ]
        },
        {
          title: '智能合约验证',
          description: '自动验证应用智能合约的安全性和可靠性',
          icon: 'Shield',
          details: [
            '代码安全审计',
            '漏洞扫描检测',
            '恶意代码识别',
            '安全等级评级'
          ]
        },
        {
          title: '垂直分类',
          description: '按垂直领域分类管理Web3应用，便于用户发现',
          icon: 'Store',
          details: [
            'DeFi应用专区',
            'NFT应用专区',
            'GameFi应用专区',
            '工具应用专区'
          ]
        }
      ],
      specifications: [
        {
          category: '平台特性',
          items: [
            { name: '应用数量', value: '1000+ Web3应用' },
            { name: '更新频率', value: '实时更新' },
            { name: '安全检测', value: '100%智能合约验证' },
            { name: '分类数量', value: '20+ 垂直领域' }
          ]
        }
      ],
      images: [
        { url: '/images/products/market-1.jpg', alt: '应用市场界面', type: 'main' },
        { url: '/images/products/market-2.jpg', alt: '应用详情页面', type: 'gallery' }
      ],
      tags: ['应用市场', '去中心化', 'Web3', '应用商店']
    },
    'call': {
      _id: 'call',
      name: '去中心化通话',
      slug: 'call',
      description: '基于钱包地址拨打作为通话目标实现全球通话，提供去中心化的通信服务，通话费用透明化。',
      shortDescription: '基于钱包地址的全球通话服务',
      category: '软件',
      price: null, // 内置功能，不显示价格
      availability: 'in_stock',
      features: [
        {
          title: '钱包地址通话',
          description: '使用钱包地址作为通话ID，无需传统电话号码',
          icon: 'Phone',
          details: [
            '钱包地址绑定',
            '去中心化身份',
            '隐私保护',
            '全球唯一标识'
          ]
        },
        {
          title: '全球网络',
          description: '基于区块链的全球通信网络，无地域限制',
          icon: 'Globe',
          details: [
            '全球节点网络',
            '低延迟通信',
            '高可用性',
            '自动路由优化'
          ]
        },
        {
          title: '加密通话',
          description: '端到端加密的通话服务，确保通信隐私',
          icon: 'Lock',
          details: [
            '端到端加密',
            '密钥自动管理',
            '防窃听保护',
            '通话记录加密'
          ]
        }
      ],
      specifications: [
        {
          category: '通信特性',
          items: [
            { name: '通话质量', value: '高清音质' },
            { name: '延迟', value: '< 100ms' },
            { name: '加密等级', value: 'AES-256' },
            { name: '支持网络', value: '全球覆盖' }
          ]
        }
      ],
      images: [
        { url: '/images/products/call-1.jpg', alt: '通话界面', type: 'main' },
        { url: '/images/products/call-2.jpg', alt: '钱包地址通话', type: 'gallery' }
      ],
      tags: ['通话', '去中心化', '加密', '全球网络']
    }
  }

  useEffect(() => {
    if (slug) {
      // 根据slug获取对应的产品数据
      const productData = products[slug]
      if (productData) {
        setProduct(productData)
      } else {
        // 如果找不到产品，使用默认产品
        setProduct(products['coretrust'])
      }
    }
  }, [slug])

  const handleBookmark = () => {
    setIsBookmarked(!isBookmarked)
  }

  const handlePreOrder = () => {
    // 跳转到联系页面进行预订
    navigate('/contact?subject=产品预订&product=' + encodeURIComponent(product?.name || ''))
  }

  const getIconComponent = (iconName) => {
    const iconMap = {
      'Smartphone': Smartphone,
      'Shield': Shield,
      'Zap': Zap,
      'Globe': Globe,
      'Phone': Phone,
      'Gift': Gift,
      'CreditCard': CreditCard,
      'Store': Store,
      'Lock': Lock,
      'Clock': Clock
    }
    return iconMap[iconName] || Smartphone
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            产品未找到
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            抱歉，您要查看的产品不存在
          </p>
          <Link
            to="/products"
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            返回产品列表
          </Link>
        </div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>{product.name} - DeepLink Limited</title>
        <meta name="description" content={product.description} />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Header */}
        <section className="py-8 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between">
              <button
                onClick={() => navigate('/products')}
                className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                返回产品列表
              </button>
              
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleBookmark}
                  className={`p-2 rounded-lg transition-colors ${
                    isBookmarked
                      ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <Heart className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Product Content */}
        <section className="py-12">
          <div className="container mx-auto px-4 max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-12"
            >
              {/* Product Images */}
              <div className="space-y-4">
                <div className="aspect-square bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden">
                  <img
                    src={product.images?.[selectedImage]?.url || '/images/products/default.jpg'}
                    alt={product.images?.[selectedImage]?.alt || product.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                
                {product.images && product.images.length > 1 && (
                  <div className="grid grid-cols-3 gap-4">
                    {product.images.map((image, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedImage(index)}
                        className={`aspect-square bg-white dark:bg-gray-800 rounded-lg overflow-hidden border-2 transition-colors ${
                          selectedImage === index
                            ? 'border-blue-600 dark:border-blue-400'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                        <img
                          src={image.url}
                          alt={image.alt}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Product Info */}
              <div className="space-y-6">
                <div>
                  <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
                    {product.name}
                  </h1>
                  <p className="text-xl text-gray-600 dark:text-gray-400 mb-6">
                    {product.shortDescription}
                  </p>
                  
                  <div className="flex items-center space-x-4 mb-6">
                    {product.price ? (
                      // 只有手机产品才显示价格
                      <div className="flex items-center">
                        <span className="text-3xl font-bold text-gray-900 dark:text-white">
                          ¥{product.price.current.toLocaleString()}
                        </span>
                        {product.price.original > product.price.current && (
                          <span className="ml-2 text-lg text-gray-500 line-through">
                            ¥{product.price.original.toLocaleString()}
                          </span>
                        )}
                      </div>
                    ) : (
                      // 软件功能显示内置标识
                      <div className="flex items-center">
                        <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                          内置功能
                        </span>
                      </div>
                    )}
                    <div className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full text-sm font-medium">
                      {product.availability === 'in_stock' ? '可用' : '预售中'}
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  {product.category === '手机' ? (
                    // 只有手机产品才显示预订按钮
                    <>
                      <button
                        onClick={handlePreOrder}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 px-6 rounded-lg font-semibold text-lg transition-colors flex items-center justify-center"
                      >
                        <ShoppingCart className="w-5 h-5 mr-2" />
                        立即预订
                      </button>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <button className="py-3 px-6 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                          联系销售
                        </button>
                        <button className="py-3 px-6 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                          下载资料
                        </button>
                      </div>
                    </>
                  ) : (
                    // 软件功能只显示了解按钮
                    <div className="space-y-4">
                      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                        <div className="flex items-center">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mr-2" />
                          <span className="text-green-800 dark:text-green-200 font-medium">
                            内置功能 - 购买CoreTrust手机即可使用
                          </span>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <Link
                          to="/products/coretrust"
                          className="py-3 px-6 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-center font-medium"
                        >
                          了解CoreTrust手机
                        </Link>
                        <button className="py-3 px-6 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                          技术文档
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2">
                  {product.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full text-sm"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-white dark:bg-gray-800">
          <div className="container mx-auto px-4 max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
                功能特性
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                {product.name}的核心功能和特性介绍
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {product.features.map((feature, index) => {
                const IconComponent = getIconComponent(feature.icon)

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="bg-gray-50 dark:bg-gray-700 rounded-2xl p-8 hover:shadow-lg transition-shadow"
                  >
                    <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-2xl flex items-center justify-center mb-6">
                      <IconComponent className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                      {feature.description}
                    </p>
                    <ul className="space-y-2">
                      {feature.details.map((detail, detailIndex) => (
                        <li key={detailIndex} className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                          <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* Specifications Section */}
        {product.specifications && product.specifications.length > 0 && (
          <section className="py-20">
            <div className="container mx-auto px-4 max-w-6xl">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="text-center mb-16"
              >
                <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
                  技术规格
                </h2>
                <p className="text-xl text-gray-600 dark:text-gray-400">
                  {product.name}的详细技术参数和规格
                </p>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {product.specifications.map((spec, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg"
                  >
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                      {spec.category}
                    </h3>
                    <div className="space-y-4">
                      {spec.items.map((item, itemIndex) => (
                        <div key={itemIndex} className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700 last:border-b-0">
                          <span className="text-gray-600 dark:text-gray-400 font-medium">
                            {item.name}
                          </span>
                          <span className="text-gray-900 dark:text-white font-semibold">
                            {item.value}
                          </span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Contact Section */}
        <section className="py-20 bg-blue-600">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {product.category === '手机' ? (
                // 手机产品的联系部分
                <>
                  <h2 className="text-4xl font-bold text-white mb-6">
                    准备体验{product.name}？
                  </h2>
                  <p className="text-xl text-blue-100 mb-8">
                    联系我们的销售团队，了解更多产品信息和预订详情
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <button
                      onClick={handlePreOrder}
                      className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors"
                    >
                      立即预订
                    </button>
                    <Link
                      to="/contact"
                      className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-blue-600 transition-colors"
                    >
                      联系销售
                    </Link>
                  </div>
                </>
              ) : (
                // 软件功能的联系部分
                <>
                  <h2 className="text-4xl font-bold text-white mb-6">
                    想了解{product.name}的更多信息？
                  </h2>
                  <p className="text-xl text-blue-100 mb-8">
                    {product.name}是CoreTrust手机的内置功能，购买手机即可体验所有功能
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link
                      to="/products/coretrust"
                      className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors"
                    >
                      了解CoreTrust手机
                    </Link>
                    <Link
                      to="/contact"
                      className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-blue-600 transition-colors"
                    >
                      联系我们
                    </Link>
                  </div>
                </>
              )}
            </motion.div>
          </div>
        </section>
      </div>
    </>
  )
}

export default ProductDetail